import { useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import {
  invalidateAllActionsQueries,
  getProcessMaterialFinishQueryKey,
} from '@frontend/api';
import { useMemoizedArray } from '@frontend/utils';

import { quotePartViewer } from 'modules/quotePartViewer/data/actions';
import {
  getQuoteNum,
  getActiveComponentId,
  getQuoteItemId,
  getQuoteRevision,
  getQuoteItemPartUuids,
} from 'modules/quotePartViewer/data/reducer';
import { assemblyTree } from 'modules/quotes/quoteItemComponents/assemblyTree/data/actions';
import {
  briefComponent,
  refreshQuoteItem,
} from 'modules/quotes/quoteItems/data/actions';
import { refreshComponent } from 'modules/quotes/quoteItems/data/actions';
import { refreshBomRollup } from 'modules/quotes/quoteItems/data/actions';
import { getSimilarPartsKey } from 'modules/quotes/quoteItems/data/hooks';
import { invalidateAllBomTableQueries } from 'modules/quotes/quoteItems/data/utils/bomTable';
import { partDetails, partFileStatuses } from 'modules/viewerPage/data/actions';
import {
  getTechnicalDataPackage,
  type PartFileInterrogationStatuses,
} from 'modules/viewerPage/data/reducer';

import {
  broadcastService,
  useSubscribeBroadcast,
} from 'services/broadcastService';

import { stores } from 'store/configureStore';
import { getExpandedQuoteItemId } from 'store/orders/selectors';
import { getBriefComponentById } from 'store/quotes/selectors';

export const QPV_TAB_SYNCING_EVENTS = {
  PART_DESCRIPTOR_CHANGE: 'partDescriptorChange', // part num, rev, description
  PART_DIMENSIONS_CHANGE: 'partDimensionsChange', // part dimensions
  EXPORT_CONTROLLED_CHANGE: 'exportControlledChange', // export controlled
  COMPONENT_COSTING_CHANGE: 'componentCostingChange', // process id, material id, supplier material id, finishes, operations
  QUOTE_ITEM_WORKFLOW_CHANGE: 'quoteItemWorkflowChange', // quote item workflow status, workflow steps, estimator
  ACTION_RESOLVED: 'actionResolved', // action resolved
} as const;

export type QuoteItemWorkflowChangeContext = {
  isRootComponent: boolean;
  quoteItemId: number;
};

export type PartDescriptorChangeContext = {
  isRootComponent: boolean;
  partUuid: string;
  componentId: number;
  quoteItemId: number;
  quoteNum: number;
  quoteRevision: number | null;
};

export type PartDescriptorChangePayload = {
  partNumber?: string | null;
  revision?: string | null;
  description?: string | null;
};

export type ExportControlledChangeContext = {
  quoteItemId: number;
};

export type ExportControlledChangePayload = {
  isExportControlled: boolean;
};

export type PartDimensionsChangeContext = {
  componentId: number;
  partUuid: string;
};

export type QpvToBaqCostingChangeContext = {
  quoteNum?: number;
  quoteItemId?: number;
  componentId?: number;
};

export type QpvToBaqCostingChangePayload = {
  componentId: number;
  parentComponentIds?: number[];
};

export type BaqToQpvCostingChangeContext = {
  partUuid?: string;
  quoteItemId?: number;
  componentId?: number;
  partFileUuids?: string[];
  interrogationStatuses?: PartFileInterrogationStatuses;
};

/**
 * Called from BAQ to sync with updates made on the QPV page
 */
export const useSyncQpvChangesWithBaq = () => {
  const dispatch = useDispatch();
  const routeMatch = useRouteMatch();
  const { quoteNumberAsNum, quoteRevisionAsNum } = useMemo(() => {
    const { quoteNumber: quoteNumAndRev } = routeMatch.params as {
      quoteNumber: string;
    };
    const revisionQuoteNumber = quoteNumAndRev?.split('-') ?? [];
    const [quoteNumber] = revisionQuoteNumber;
    const num = parseInt(quoteNumber, 10);
    const revision = revisionQuoteNumber[1]
      ? parseInt(revisionQuoteNumber[1], 10)
      : null;
    return {
      quoteNumberAsNum: isNaN(num) ? null : num,
      quoteRevisionAsNum: revision != null && isNaN(revision) ? null : revision,
    };
  }, [routeMatch.params]);
  const expandedQuoteItemId = useSelector(getExpandedQuoteItemId);
  const queryClient = useQueryClient();

  // Listen for changes sourced from QPV windows
  useSubscribeBroadcast<null, null>(QPV_TAB_SYNCING_EVENTS.ACTION_RESOLVED, {
    next: () => {
      invalidateAllActionsQueries();
    },
  });

  // Listen for changes sourced from QPV windows
  useSubscribeBroadcast<
    PartDescriptorChangePayload,
    PartDescriptorChangeContext
  >(
    QPV_TAB_SYNCING_EVENTS.PART_DESCRIPTOR_CHANGE,
    {
      next: ({ context }) => {
        if (
          !context ||
          context.quoteNum !== quoteNumberAsNum ||
          context.quoteRevision !== quoteRevisionAsNum
        ) {
          return;
        }

        const { componentId } = context;

        dispatch(refreshComponent({ componentId }));
        dispatch(briefComponent.refresh({ componentId }));
        dispatch(assemblyTree.markNeedsUpdate({}));
      },
    },
    [quoteNumberAsNum, quoteRevisionAsNum],
  );

  useSubscribeBroadcast<null, PartDimensionsChangeContext>(
    QPV_TAB_SYNCING_EVENTS.PART_DIMENSIONS_CHANGE,
    {
      next: ({ context }) => {
        if (!context) {
          return;
        }

        dispatch(refreshComponent({ componentId: context.componentId }));
        invalidateAllBomTableQueries();
        queryClient.invalidateQueries(getSimilarPartsKey(context.componentId));
      },
    },
    [quoteNumberAsNum, quoteRevisionAsNum],
  );

  useSubscribeBroadcast<null, ExportControlledChangeContext>(
    QPV_TAB_SYNCING_EVENTS.EXPORT_CONTROLLED_CHANGE,
    {
      next: ({ context }) => {
        if (!context) {
          return;
        }

        // refresh quote item since pricing can change
        dispatch(
          refreshQuoteItem({
            quoteItemId: context.quoteItemId,
            shouldRecalculate: false,
          }),
        );
      },
    },
    [quoteNumberAsNum, quoteRevisionAsNum],
  );

  // Listen for changes sourced from QPV windows
  useSubscribeBroadcast<
    QpvToBaqCostingChangePayload,
    QpvToBaqCostingChangeContext
  >(
    QPV_TAB_SYNCING_EVENTS.COMPONENT_COSTING_CHANGE,
    {
      next: ({ context, payload }) => {
        const { componentId, parentComponentIds } = payload ?? {};
        if (
          !componentId ||
          !context ||
          context.quoteNum !== quoteNumberAsNum ||
          (!!expandedQuoteItemId && context.quoteItemId !== expandedQuoteItemId)
        ) {
          return;
        }

        dispatch(refreshComponent({ componentId, params: {} }));

        if (!parentComponentIds) {
          const state = stores.global?.getState();
          if (!state) {
            return;
          }
          const { parentComponentIds: storedParentIds } =
            getBriefComponentById(state, componentId) ?? {};
          if (storedParentIds?.length > 0) {
            dispatch(
              refreshBomRollup.request({ parentComponentIds: storedParentIds }),
            );
          }
        } else {
          if (parentComponentIds.length > 0) {
            dispatch(refreshBomRollup.request({ parentComponentIds }));
          }
        }
      },
    },
    [expandedQuoteItemId, quoteNumberAsNum],
  );

  useSubscribeBroadcast<null, QuoteItemWorkflowChangeContext>(
    QPV_TAB_SYNCING_EVENTS.QUOTE_ITEM_WORKFLOW_CHANGE,
    {
      next: ({ context }) => {
        if (!context) {
          return;
        }

        if (context.isRootComponent) {
          dispatch(
            refreshQuoteItem({
              quoteItemId: context.quoteItemId,
              shouldRecalculate: false,
            }),
          );
        }
      },
    },
    [expandedQuoteItemId],
  );
};

export const usePropagateExportControlledStatusToViewerPage = () => {
  const dispatch = useDispatch();
  const technicalDataPackage = useSelector(getTechnicalDataPackage);

  const propagateControlledStatusToViewerPage = useCallback(
    ({
      quoteItemId,
      isExportControlled,
    }: {
      quoteItemId: number;
      isExportControlled: boolean;
    }) => {
      // update quote item if root component by manually inserting payload data
      // TODO: we should just be able to refetch this data instead of manually inserting
      // payload data
      dispatch(
        quotePartViewer.receiveQuoteItemUpdate({
          quoteItem: {
            id: quoteItemId,
            exportControlled: isExportControlled,
          },
        }),
      );

      // refresh export controlled status in technical data package since surrounding
      //  viewer page relies on that
      // do not use receive tech data package because it has other side effects we do not want
      const nextTechnicalDataPackage = {
        ...technicalDataPackage,
        isExportControlled: isExportControlled,
      };
      dispatch(partDetails.updateSuccess(nextTechnicalDataPackage));
    },
    [technicalDataPackage, dispatch],
  );

  return propagateControlledStatusToViewerPage;
};

/**
 * Called from QPV to sync with updates made on the BAQ page
 */
export const useSyncBaqChangesWithQpv = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const quoteNum = useSelector(getQuoteNum);
  const quoteRevision = useSelector(getQuoteRevision) ?? null;
  const activeComponentId = useSelector(getActiveComponentId);
  const quoteItemId = useSelector(getQuoteItemId);
  const quoteItemPartUuids = useSelector(getQuoteItemPartUuids);
  const memoizedPartUuids = useMemoizedArray(quoteItemPartUuids);

  // process, material, operations list
  useSubscribeBroadcast<null, BaqToQpvCostingChangeContext>(
    QPV_TAB_SYNCING_EVENTS.COMPONENT_COSTING_CHANGE,
    {
      next: ({ context }) => {
        if (!context) {
          return;
        }

        const {
          componentId,
          interrogationStatuses,
          partUuid: partUUID,
          quoteItemId,
        } = context;

        // invalidate if the costing change is for current active component. We need to refresh
        // because the actions resolutions may change based on active process and operation list
        if (componentId && componentId === activeComponentId) {
          // NB: Normally we would only invalidate the actions query for the active component, but since the action query can also contain filter values,
          // which are provided locally within the RR module, we need to invalidate all actions queries to guarantee an action list refresh
          invalidateAllActionsQueries();
        }

        if (partUUID) {
          const componentDetailsQueryKey = getProcessMaterialFinishQueryKey({
            partUUID,
            quoteItemId,
          });
          queryClient.invalidateQueries({ queryKey: componentDetailsQueryKey });
        }

        if (interrogationStatuses) {
          dispatch(
            partFileStatuses.receiveInterrogationStatuses({
              interrogationStatuses,
            }),
          );
        }
      },
    },
    [activeComponentId],
  );

  // part num, rev, description
  useSubscribeBroadcast<
    PartDescriptorChangePayload,
    PartDescriptorChangeContext
  >(
    QPV_TAB_SYNCING_EVENTS.PART_DESCRIPTOR_CHANGE,
    {
      next: ({ context, payload }) => {
        if (
          !payload ||
          !context ||
          context.quoteNum !== quoteNum ||
          context.quoteRevision !== quoteRevision ||
          !memoizedPartUuids?.includes(context.partUuid)
        ) {
          return;
        }

        // update core viewer page data by refetching
        dispatch(partDetails.get({ partUuid: context.partUuid }));
        // update quote item if root component by manually inserting payload data
        // TODO: we should just be able to refetch this data instead of manually inserting
        // payload data
        if (context.isRootComponent) {
          dispatch(
            quotePartViewer.receiveQuoteItemUpdate({
              quoteItem: {
                id: context.quoteItemId,
                ...(payload.partNumber != null && {
                  partNumber: payload.partNumber,
                }),
                ...(payload.revision != null && { revision: payload.revision }),
                ...(payload.description != null && {
                  description: payload.description,
                }),
              },
            }),
          );
        }
      },
    },
    [quoteNum, quoteRevision, memoizedPartUuids],
  );

  // part dimensions
  useSubscribeBroadcast<null, PartDimensionsChangeContext>(
    QPV_TAB_SYNCING_EVENTS.PART_DIMENSIONS_CHANGE,
    {
      next: ({ context }) => {
        if (!context) {
          return;
        }

        if (!memoizedPartUuids?.includes(context.partUuid)) {
          return;
        }

        // update core viewer page data by refetching
        dispatch(partDetails.get({ partUuid: context.partUuid }));
      },
    },
    [memoizedPartUuids],
  );

  const propagateControlledStatusToViewerPage =
    usePropagateExportControlledStatusToViewerPage();

  // quote item export controlled status
  useSubscribeBroadcast<
    ExportControlledChangePayload,
    ExportControlledChangeContext
  >(
    QPV_TAB_SYNCING_EVENTS.EXPORT_CONTROLLED_CHANGE,
    {
      next: ({ context, payload }) => {
        if (!payload || !context) {
          return;
        }
        if (context.quoteItemId !== quoteItemId) {
          return;
        }

        propagateControlledStatusToViewerPage({
          quoteItemId: context.quoteItemId,
          isExportControlled: payload.isExportControlled,
        });
      },
    },
    [quoteItemId],
  );

  // listen for changes to workflow status and estimator id
  useSubscribeBroadcast<null, QuoteItemWorkflowChangeContext>(
    QPV_TAB_SYNCING_EVENTS.QUOTE_ITEM_WORKFLOW_CHANGE,
    {
      next: ({ context }) => {
        if (!context) {
          return;
        }

        // only invalidate if change is on active quote item. We need to refresh because
        // workflow status, estimator assignment, and workflow steps can influence resolutions
        if (
          activeComponentId &&
          context.quoteItemId &&
          context.isRootComponent &&
          String(context.quoteItemId) === String(quoteItemId)
        ) {
          // NB: Normally we would only invalidate the actions query for the active component, but since the action query can also contain filter values,
          // which are provided locally within the RR module, we need to invalidate all actions queries to guarantee an action list refresh
          invalidateAllActionsQueries();
        }
      },
    },
    [activeComponentId, quoteItemId],
  );
};

/**
 * Broadcasts a component costing change to the QPV - for use in sagas, services functions, and
 * components
 */
export const broadcastComponentCostingChangeFromBaqToQpv = ({
  context,
}: {
  context: BaqToQpvCostingChangeContext;
}) => {
  broadcastService.dispatch<null, BaqToQpvCostingChangeContext>({
    action: QPV_TAB_SYNCING_EVENTS.COMPONENT_COSTING_CHANGE,
    context,
  });
};

/**
 * Broadcasts a component costing change to the BAQ - for use in sagas, services functions, and
 * components
 */
export const broadcastComponentCostingChangeFromQpvToBaq = ({
  context,
  payload,
}: {
  context: QpvToBaqCostingChangeContext;
  payload: QpvToBaqCostingChangePayload;
}) => {
  broadcastService.dispatch<
    QpvToBaqCostingChangePayload,
    QpvToBaqCostingChangeContext
  >({
    action: QPV_TAB_SYNCING_EVENTS.COMPONENT_COSTING_CHANGE,
    context,
    payload,
  });
};

/**
 * Broadcasts a part descriptor change to either BAQ or QPV - for use in sagas, services
 * functions, and components
 */
export const broadcastPartDescriptorChange = ({
  context,
  payload,
}: {
  context: PartDescriptorChangeContext;
  payload: PartDescriptorChangePayload;
}) => {
  broadcastService.dispatch<
    PartDescriptorChangePayload,
    PartDescriptorChangeContext
  >({
    action: QPV_TAB_SYNCING_EVENTS.PART_DESCRIPTOR_CHANGE,
    context,
    payload,
  });
};

/**
 * Broadcasts a quote item workflow change to the QPV or BAQ - for use in sagas, services
 * functions, and components
 */
export const broadcastQuoteItemWorkflowChange = ({
  context,
}: {
  context: QuoteItemWorkflowChangeContext;
}) => {
  broadcastService.dispatch<null, QuoteItemWorkflowChangeContext>({
    action: QPV_TAB_SYNCING_EVENTS.QUOTE_ITEM_WORKFLOW_CHANGE,
    context,
  });
};

/**
 * Broadcasts a quote item workflow change to the QPV or BAQ - for use in sagas, services
 * functions, and components
 */
export const broadcastActionResolved = () => {
  broadcastService.dispatch<null, null>({
    action: QPV_TAB_SYNCING_EVENTS.ACTION_RESOLVED,
  });
};

/**
 * Broadcasts a part dimensions change to the QPV or BAQ - for use in sagas, services
 * functions, and components
 */
export const broadcastPartDimensionsChange = ({
  context,
}: {
  context: PartDimensionsChangeContext;
}) => {
  broadcastService.dispatch<null, PartDimensionsChangeContext>({
    action: QPV_TAB_SYNCING_EVENTS.PART_DIMENSIONS_CHANGE,
    context,
  });
};

/**
 * Broadcasts an export controlled change to the QPV or BAQ - for use in sagas, services
 * functions, and components
 */
export const broadcastExportControlledChange = ({
  context,
  payload,
}: {
  context: ExportControlledChangeContext;
  payload: ExportControlledChangePayload;
}) => {
  broadcastService.dispatch<
    ExportControlledChangePayload,
    ExportControlledChangeContext
  >({
    action: QPV_TAB_SYNCING_EVENTS.EXPORT_CONTROLLED_CHANGE,
    context,
    payload,
  });
};
